/* =========================================================
   Announcement bar – <nx-announcement>
   Design 1: seamless marquee (fills the width, speed in px/s)
   Design 2: rotating split bar, countdown, dismiss, sticky offset
   Publishes --nx-announce-h on <html> so a sticky header can sit
   right underneath a sticky announcement bar.
   ========================================================= */
(function () {
  'use strict';

  if (customElements.get('nx-announcement')) return;

  function setOffset() {
    var total = 0;
    document.querySelectorAll('nx-announcement[data-sticky="true"]').forEach(function (bar) {
      if (bar.classList.contains('is-dismissed')) return;
      if (bar.offsetParent === null && getComputedStyle(bar).position !== 'fixed') return;
      total += bar.getBoundingClientRect().height;
    });
    document.documentElement.style.setProperty('--nx-announce-h', Math.round(total) + 'px');
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function parseDate(raw) {
    if (!raw) return null;
    var match = String(raw).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
    if (!match) return null;
    return new Date(
      parseInt(match[1], 10),
      parseInt(match[2], 10) - 1,
      parseInt(match[3], 10),
      match[4] ? parseInt(match[4], 10) : 23,
      match[5] ? parseInt(match[5], 10) : 59,
      0
    );
  }

  class NxAnnouncement extends HTMLElement {
    connectedCallback() {
      this.design = this.dataset.design;
      this.designMode = this.dataset.designMode === 'true';
      this.timers = [];
      this.index = 0;

      if (this.dataset.dismissible === 'true' && !this.designMode && this.isDismissed()) {
        this.classList.add('is-dismissed');
        setOffset();
        return;
      }

      if (this.design === 'marquee') {
        this.setupMarquee();
      } else {
        this.setupSlides();
        this.setupCountdown();
        this.setupDismiss();
      }

      this.onResize = this.onResize.bind(this);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      setOffset();
    }

    disconnectedCallback() {
      this.timers.forEach(function (timer) {
        clearInterval(timer);
        clearTimeout(timer);
      });
      this.timers = [];
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.requestAnimationFrame(setOffset);
    }

    onResize() {
      if (this.design === 'marquee') this.fillMarquee();
      setOffset();
    }

    /* ---------------- Design 1: marquee ---------------- */
    setupMarquee() {
      this.track = this.querySelector('[data-track]');
      this.group = this.querySelector('[data-group]');
      if (!this.track || !this.group) return;
      this.setAttribute('data-direction', this.dataset.direction || 'left');
      this.fillMarquee();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.fillMarquee.bind(this));
      }
    }

    fillMarquee() {
      if (!this.track || !this.group) return;
      var groupWidth = this.group.getBoundingClientRect().width;
      if (!groupWidth) return;

      this.track.querySelectorAll('[data-clone]').forEach(function (clone) {
        clone.remove();
      });

      var available = this.getBoundingClientRect().width || window.innerWidth;
      var copies = Math.max(2, Math.ceil((available * 2) / groupWidth) + 1);
      for (var i = 1; i < copies; i++) {
        var clone = this.group.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (el) {
          el.removeAttribute('data-shopify-editor-block');
        });
        this.track.appendChild(clone);
      }

      var speed = parseFloat(this.dataset.speed) || 60;
      this.track.style.setProperty('--nxa-distance', groupWidth + 'px');
      this.track.style.setProperty('--nxa-duration', (groupWidth / speed).toFixed(2) + 's');
      this.track.classList.add('is-running');
    }

    /* ---------------- Design 2: rotating slides ---------------- */
    setupSlides() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      var self = this;
      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1, true); });
      if (next) next.addEventListener('click', function () { self.go(self.index + 1, true); });

      this.addEventListener('mouseenter', function () { self.hovering = true; });
      this.addEventListener('mouseleave', function () { self.hovering = false; });
      this.addEventListener('focusin', function () { self.hovering = true; });
      this.addEventListener('focusout', function () { self.hovering = false; });

      this.start();
    }

    start() {
      this.stop();
      var self = this;
      var interval = (parseInt(this.dataset.interval, 10) || 4) * 1000;
      this.autoplay = setInterval(function () {
        if (self.hovering || self.locked) return;
        self.go(self.index + 1);
      }, interval);
      this.timers.push(this.autoplay);
    }

    stop() {
      if (this.autoplay) clearInterval(this.autoplay);
    }

    go(target, manual) {
      if (!this.slides || this.slides.length < 2) return;
      var next = (target + this.slides.length) % this.slides.length;
      if (next === this.index) return;
      var current = this.slides[this.index];
      var incoming = this.slides[next];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      var timer = setTimeout(function () {
        current.classList.remove('is-leaving');
      }, 600);
      this.timers.push(timer);

      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
      this.index = next;
      if (manual) this.start();
    }

    /* ---------------- Countdown ---------------- */
    setupCountdown() {
      var box = this.querySelector('[data-countdown]');
      if (!box) return;
      var end = parseDate(box.dataset.countdown);
      if (!end || isNaN(end.getTime())) return;

      var parts = {
        d: box.querySelector('[data-d]'),
        h: box.querySelector('[data-h]'),
        m: box.querySelector('[data-m]'),
        s: box.querySelector('[data-s]')
      };
      var keepZero = box.dataset.expired === 'zero';
      var self = this;

      function tick() {
        var diff = Math.max(0, end.getTime() - Date.now());
        if (diff === 0 && !keepZero) {
          box.hidden = true;
          clearInterval(self.countdownTimer);
          setOffset();
          return;
        }
        var total = Math.floor(diff / 1000);
        parts.d.textContent = pad(Math.floor(total / 86400));
        parts.h.textContent = pad(Math.floor((total % 86400) / 3600));
        parts.m.textContent = pad(Math.floor((total % 3600) / 60));
        parts.s.textContent = pad(total % 60);
        box.hidden = false;
      }

      tick();
      this.countdownTimer = setInterval(tick, 1000);
      this.timers.push(this.countdownTimer);
    }

    /* ---------------- Dismiss ---------------- */
    storageKey() {
      return this.dataset.dismissKey || 'nxa-dismissed';
    }

    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.storageKey()) === '1';
      } catch (error) {
        return false;
      }
    }

    setupDismiss() {
      var button = this.querySelector('[data-close]');
      if (!button) return;
      var self = this;
      button.addEventListener('click', function () {
        self.classList.add('is-dismissed');
        if (!self.designMode) {
          try {
            window.sessionStorage.setItem(self.storageKey(), '1');
          } catch (error) {
            /* storage unavailable – close for this page view only */
          }
        }
        setOffset();
        if (self.designMode) {
          setTimeout(function () {
            self.classList.remove('is-dismissed');
            setOffset();
          }, 1500);
        }
      });
    }

    /* ---------------- Theme editor ---------------- */
    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      if (this.design === 'marquee') {
        this.classList.add('is-paused');
        return;
      }
      this.locked = true;
      var id = event.target.dataset.blockId;
      if (!this.slides) return;
      var target = this.slides.findIndex(function (slide) {
        return slide.dataset.blockId === id;
      });
      if (target > -1) this.go(target);
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.classList.remove('is-paused');
      this.locked = false;
    }
  }

  customElements.define('nx-announcement', NxAnnouncement);

  window.addEventListener('load', setOffset);
  document.addEventListener('shopify:section:unload', function () {
    window.requestAnimationFrame(setOffset);
  });
})();
